import { createContext, useContext, useEffect, useState } from 'react';
import { Common, DiscordSDK, DiscordSDKMock, type IDiscordSDK } from '@discord/embedded-app-sdk';
import type { DiscordGameType } from './games';

// The Discord session, apart from its provider component (DiscordSession.tsx), so that file exports only a
// component: Fast Refresh can then update it in place, where a mixed module forces a full reload, which loses
// the connection to Discord.

export interface DiscordSession {
  sdk: IDiscordSDK;
  /** The game whose activity this is. */
  gameType: DiscordGameType;
  username: string;
  /** The player id to send as X-Inkwell-User; see /api/discord/token. */
  userStr: string;
  /** The channel the activity was launched in, whose leaderboard the player is on. */
  channelId: string;
  /** Proof of `gameType` and `channelId`, for the leaderboard and solve endpoints; see signChannelToken. */
  channelToken: string;
}

export type SessionState =
  | { state: 'pending'; step: string }
  | { state: 'error'; message: string }
  | DiscordSessionState;
type DiscordSessionState = { state: 'signedIn'; session: DiscordSession };

/*
  Once per page load. Discord answers the SDK's handshake only once per iframe and rejects a second concurrent
  authorize() ("already authing"), but DiscordSessionProvider's effect runs twice under Strict Mode, and hot
  reloading re-runs this module. Kept on globalThis rather than in a module variable, so a re-run module picks up
  the existing connection instead of opening one Discord never answers.
*/
const pageGlobals = globalThis as { discordSignIn?: Promise<SessionState> };

const READY_TIMEOUT_MS = 15 * 1000;

/** Starts signing in, or returns the sign-in already under way for this page. */
export function signInOnce(
  gameType: DiscordGameType,
  clientId: string,
  setState: (s: SessionState) => void
): Promise<SessionState> {
  pageGlobals.discordSignIn ??= signIn(gameType, clientId, setState).catch((err) => {
    console.error('[discord] sign-in failed:', err);
    return { state: 'error', message: err instanceof Error ? err.message : JSON.stringify(err) };
  });
  return pageGlobals.discordSignIn;
}

async function signIn(
  gameType: DiscordGameType,
  clientId: string,
  setState: (s: SessionState) => void
): Promise<DiscordSessionState> {
  // In development outside Discord, a mock SDK and a fixed player, so the pages can be worked on in a plain
  // browser at localhost:3000/discord/<game>.
  if (process.env.NODE_ENV === 'development' && !new URLSearchParams(window.location.search).has('frame_id')) {
    const res = await fetch('/api/discord/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game: gameType, localDev: true }),
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
    const sdk = new DiscordSDKMock(clientId, null, null, null);
    return {
      state: 'signedIn',
      session: {
        sdk,
        gameType,
        username: 'Local Dev',
        userStr: body.userStr,
        channelId: body.channelId,
        channelToken: body.channelToken,
      },
    };
  }
  // Throws outside Discord, where the frame_id query param is missing.
  const sdk = new DiscordSDK(clientId);
  setState({ state: 'pending', step: 'connecting to Discord' });
  await Promise.race([
    sdk.ready(),
    // Discord answers only the first handshake from an activity's iframe, so a page reloaded in place (by a
    // development full reload, say) waits forever; say so instead.
    new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error('Lost the connection to Discord. Close the activity and launch it again.')),
        READY_TIMEOUT_MS
      )
    ),
  ]);

  // Shows Discord's consent prompt the first time; after that it returns a code without asking.
  setState({ state: 'pending', step: 'authorizing' });
  const { code } = await sdk.commands.authorize({
    client_id: clientId,
    response_type: 'code',
    state: '',
    prompt: 'none',
    scope: ['identify'],
  });

  setState({ state: 'pending', step: 'exchanging code' });
  const res = await fetch('/api/discord/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ game: gameType, code, instanceId: sdk.instanceId }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);

  setState({ state: 'pending', step: 'authenticating' });
  const { user } = await sdk.commands.authenticate({ access_token: body.accessToken });
  return {
    state: 'signedIn',
    session: {
      sdk,
      gameType,
      username: user.global_name ?? user.username,
      userStr: body.userStr,
      channelId: body.channelId,
      channelToken: body.channelToken,
    },
  };
}

export const SessionContext = createContext<SessionState | null>(null);

/** The sign-in's progress, for DiscordSignedIn and anything else rendered before it completes. */
export function useDiscordSessionState(): SessionState {
  const state = useContext(SessionContext);
  if (!state) throw new Error('useDiscordSessionState must be used within a DiscordSessionProvider');
  return state;
}

/** The signed-in session. Only for components rendered inside DiscordSignedIn. */
export function useDiscordSession(): DiscordSession {
  const state = useDiscordSessionState();
  if (state.state !== 'signedIn') throw new Error('useDiscordSession must be used within DiscordSignedIn');
  return state.session;
}

/**
 * Opens a URL outside the activity. A plain link would navigate the activity's own iframe; Discord opens this in
 * the player's browser, after its usual leaving-Discord prompt.
 */
export function useOpenExternalLink(): (url: string) => void {
  const state = useDiscordSessionState();
  return (url) => {
    // Before sign-in completes there's no SDK to ask yet.
    if (state.state !== 'signedIn') return;
    const { sdk } = state.session;
    if (sdk instanceof DiscordSDKMock) window.open(url, '_blank', 'noopener');
    else void sdk.commands.openExternalLink({ url });
  };
}

/** How Discord is showing the activity: full size, as its small floating tile, or as a tile in a call's grid. */
export type DiscordLayoutMode = 'focused' | 'pip' | 'grid';

const LAYOUT_MODES: Record<number, DiscordLayoutMode> = {
  [Common.LayoutModeTypeObject.PIP]: 'pip',
  [Common.LayoutModeTypeObject.GRID]: 'grid',
};

/**
 * The activity's current layout mode. Discord reports only changes, from the focused mode an activity launches in.
 * The local development session takes it from a `layout` query param (?layout=pip), as the mock SDK never reports one.
 */
export function useDiscordLayoutMode(): DiscordLayoutMode {
  const { sdk } = useDiscordSession();
  const [mode, setMode] = useState<DiscordLayoutMode>(() => {
    if (!(sdk instanceof DiscordSDKMock)) return 'focused';
    const param = new URLSearchParams(window.location.search).get('layout');
    return param === 'pip' || param === 'grid' ? param : 'focused';
  });

  useEffect(() => {
    // A mode the SDK doesn't know is taken as focused, so the puzzle is never hidden by mistake.
    const onUpdate = ({ layout_mode }: { layout_mode: number }) => setMode(LAYOUT_MODES[layout_mode] ?? 'focused');
    sdk
      .subscribe('ACTIVITY_LAYOUT_MODE_UPDATE', onUpdate)
      .catch((err) => console.warn('[discord] no layout mode updates:', err));
    return () => void sdk.unsubscribe('ACTIVITY_LAYOUT_MODE_UPDATE', onUpdate).catch(() => {});
  }, [sdk]);

  return mode;
}
